// SP3a — the single, shared transition control used by BOTH editor surfaces
// (the detail-page program/items editor and the layout-editor zone playlist),
// for the loop default (`mode="default"`) and per-item overrides
// (`mode="item"`). One component so the two surfaces can never drift apart
// again. The transition DATA model is unchanged: { effect, duration_ms },
// resolution item-override -> loop-default -> cut.

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Command } from 'cmdk';
import { TransitionEffects } from '@displaycms/contracts';
import type { Transition, TransitionEffect } from '@displaycms/contracts';
import { Input } from '@/components/ui/input';
import { useComboboxDismiss } from '@/hooks/use-combobox-dismiss';
import { cn } from '@/lib/utils';

const MIN_MS = 100;
const MAX_MS = 2000;
const DEFAULT_MS = 500;
// Radix Select forbids an empty-string item value — use a sentinel for the
// "no transition" / "inherit" choice.
const NONE = '__none__';
// 'cut' IS a TransitionEffect ("no transition"), but it's represented by the
// none/inherit option, so it's not offered as a distinct animated effect.
const EFFECT_OPTIONS = TransitionEffects.filter((e) => e !== 'cut');

/** Resolve the transition that actually applies: item override, else the loop
 *  default, else null (a hard cut). Mirrors the player's resolution order. */
export function effectiveTransition(
  override: Transition | undefined,
  loopDefault: Transition | null | undefined,
): Transition | null {
  return override ?? loopDefault ?? null;
}

function inheritLabel(inherited: Transition | null | undefined, t: TFunction): string {
  if (!inherited || inherited.effect === 'cut') {
    return t('transition_inherit_none', { defaultValue: 'Hériter (Aucune)' });
  }
  const eff = t(`transition_effect_${inherited.effect}`, { defaultValue: inherited.effect });
  return t('transition_inherit_fmt', {
    effect: eff,
    ms: inherited.duration_ms,
    defaultValue: `Hériter (${eff} · ${inherited.duration_ms} ms)`,
  });
}

export interface TransitionControlProps {
  value: Transition | undefined;
  mode: 'default' | 'item';
  /** For mode="item": the loop default, shown in the "Hériter (…)" option. */
  inheritedFrom?: Transition | null;
  onChange: (next: Transition | undefined) => void;
  disabled?: boolean;
}

export function TransitionControl({
  value,
  mode,
  inheritedFrom,
  onChange,
  disabled,
}: TransitionControlProps): JSX.Element {
  const { t } = useTranslation('composers');
  // An existing {effect:'cut'} shows as the none/inherit state — we never
  // silently rewrite it; onChange only fires on real user interaction.
  const selected = value && value.effect !== 'cut' ? value.effect : NONE;
  const noneLabel =
    mode === 'default'
      ? t('transition_none', { defaultValue: 'Aucune (coupe)' })
      : inheritLabel(inheritedFrom, t);

  // UX audit — searchable typeahead replacing the kit <Select>. The VALUE model
  // is unchanged: the trigger emits the same effect string (or the NONE
  // sentinel) into `emitEffect`, which is byte-for-byte the old behaviour.
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useComboboxDismiss(wrapRef, open, () => setOpen(false));

  const selectedLabel =
    selected === NONE ? noneLabel : t(`transition_effect_${selected}`, { defaultValue: selected });

  function emitEffect(eff: string): void {
    if (eff === NONE) {
      onChange(undefined);
      return;
    }
    onChange({ effect: eff as TransitionEffect, duration_ms: value?.duration_ms ?? DEFAULT_MS });
  }

  function onOptionSelect(eff: string): void {
    emitEffect(eff);
    setOpen(false);
  }

  function emitDuration(raw: number): void {
    if (!value || value.effect === 'cut' || Number.isNaN(raw)) return;
    const clamped = Math.min(MAX_MS, Math.max(MIN_MS, Math.round(raw)));
    onChange({ effect: value.effect, duration_ms: clamped });
  }

  return (
    <div className="flex items-center gap-1.5">
      <div className="relative" ref={wrapRef}>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled ?? false}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            'flex h-8 w-auto min-w-[9rem] items-center justify-between gap-1 rounded-md border border-input bg-background px-3 text-xs',
            'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <span className="truncate">{selectedLabel}</span>
          <span aria-hidden className="ml-1 opacity-60">▾</span>
        </button>
        {open && (
          <Command
            className="absolute z-50 mt-1 w-56 rounded-md border bg-popover text-xs shadow-md"
            filter={(value, search) =>
              value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
            }
          >
            <Command.Input
              placeholder={t('picker.search_placeholder', { defaultValue: 'Rechercher…' })}
              autoFocus
              className="w-full border-b bg-transparent px-2 py-1.5 text-xs outline-none"
            />
            <Command.List className="max-h-48 overflow-y-auto p-1">
              <Command.Empty className="px-2 py-1.5 text-muted-foreground">—</Command.Empty>
              <Command.Item
                value={noneLabel}
                onSelect={() => onOptionSelect(NONE)}
                className="cursor-pointer rounded px-2 py-1.5 aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                {noneLabel}
              </Command.Item>
              {EFFECT_OPTIONS.map((eff) => {
                const label = t(`transition_effect_${eff}`, { defaultValue: eff });
                return (
                  <Command.Item
                    key={eff}
                    value={label}
                    onSelect={() => onOptionSelect(eff)}
                    className="cursor-pointer rounded px-2 py-1.5 aria-selected:bg-accent aria-selected:text-accent-foreground"
                  >
                    {label}
                  </Command.Item>
                );
              })}
            </Command.List>
          </Command>
        )}
      </div>
      {selected !== NONE && (
        <Input
          type="number"
          min={MIN_MS}
          max={MAX_MS}
          step={100}
          value={value?.duration_ms ?? DEFAULT_MS}
          onChange={(e) => emitDuration(Number(e.target.value))}
          disabled={disabled}
          aria-label={t('transition_duration_label', { defaultValue: 'Durée (ms)' })}
          className="h-8 w-20 text-xs"
        />
      )}
    </div>
  );
}
